import { useState, useEffect } from 'react';
import { Card, Tabs, Input, Button, message, List, Modal, Form } from 'antd';
import { PlusOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';

const { TextArea } = Input;
const { TabPane } = Tabs;

const API_URL = process.env.REACT_APP_API_URL;

interface TiaData {
    typeOfTravelAgentList: string[];
    originCityList: string[];
    chinaCityList: { province: string; cities: string[] }[];
    currencyList: string[];
    controlPointList: string[];
    typeOfFacilityList: string[];
    facilityList: { facilityType: string; facility: string[] }[];
    typeOfAccommdation: string[];
}

function TiaDataManagement() {
    const [tiaData, setTiaData] = useState<TiaData | null>(null);
    const [editingList, setEditingList] = useState<string | null>(null);
    const [editModalVisible, setEditModalVisible] = useState(false);
    const [editingIndex, setEditingIndex] = useState<number>(-1);
    const [form] = Form.useForm();

    useEffect(() => {
        fetchTiaData();
    }, []);

    const fetchTiaData = async () => {
        try {
            const response = await fetch(`${API_URL}/admin/tia-data`, {
                credentials: 'include',
            });
            const result = await response.json();
            if (result.success) {
                setTiaData(result.data);
            } else {
                message.error('Failed to fetch TIA data');
            }
        } catch (err) {
            message.error('Error fetching TIA data');
            console.error(err);
        }
    };

    const updateList = async (listName: string, values: any) => {
        try {
            const response = await fetch(`${API_URL}/admin/tia-data/update-list`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ listName, values }),
            });
            const result = await response.json();
            if (result.success) {
                message.success(`${listName} updated successfully`);
                fetchTiaData();
            } else {
                message.error(result.message || 'Failed to update list');
            }
        } catch (err) {
            message.error('Error updating list');
            console.error(err);
        }
    };

    const handleAddItem = (listName: string) => {
        setEditingList(listName);
        setEditingIndex(-1);
        
        if (listName === 'chinaCityList') {
            form.setFieldsValue({ province: '', cities: [] });
        } else if (listName === 'facilityList') {
            form.setFieldsValue({ facilityType: '', facility: [] });
        } else {
            form.setFieldsValue({ value: '' });
        }
        
        setEditModalVisible(true);
    };

    const handleEditItem = (listName: string, item: any, index: number) => {
        setEditingList(listName);
        setEditingIndex(index);
        
        if (listName === 'chinaCityList') {
            form.setFieldsValue({ province: item.province, cities: item.cities });
        } else if (listName === 'facilityList') {
            form.setFieldsValue({ facilityType: item.facilityType, facility: item.facility });
        } else {
            form.setFieldsValue({ value: item });
        }
        
        setEditModalVisible(true);
    };

    const handleDeleteItem = (listName: string, index: number) => {
        if (!tiaData) return;
        
        Modal.confirm({
            title: 'Confirm Delete',
            content: 'Are you sure you want to delete this item?',
            onOk: () => {
                const newValues = [...(tiaData[listName as keyof TiaData] as any[])];
                newValues.splice(index, 1);
                updateList(listName, newValues);
            },
        });
    };

    const handleModalOk = () => {
        form.validateFields().then((values) => {
            if (!tiaData || !editingList) return;

            const currentList = [...(tiaData[editingList as keyof TiaData] as any[])];
            
            let newItem: any;
            if (editingList === 'chinaCityList') {
                newItem = {
                    province: values.province,
                    cities: values.cities.split('\n').filter((c: string) => c.trim()),
                };
            } else if (editingList === 'facilityList') {
                newItem = {
                    facilityType: values.facilityType,
                    facility: values.facility.split('\n').filter((f: string) => f.trim()),
                };
            } else {
                newItem = values.value;
            }

            if (editingIndex >= 0) {
                currentList[editingIndex] = newItem;
            } else {
                currentList.push(newItem);
            }

            updateList(editingList, currentList);
            setEditModalVisible(false);
            form.resetFields();
        });
    };

    const renderSimpleList = (listName: string, data: string[]) => (
        <Card
            title={listName}
            extra={
                <Button type="primary" icon={<PlusOutlined />} onClick={() => handleAddItem(listName)}>
                    Add Item
                </Button>
            }
        >
            <List
                dataSource={data}
                renderItem={(item, index) => (
                    <List.Item
                        actions={[
                            <Button
                                type="link"
                                icon={<EditOutlined />}
                                onClick={() => handleEditItem(listName, item, index)}
                            >
                                Edit
                            </Button>,
                            <Button
                                type="link"
                                danger
                                icon={<DeleteOutlined />}
                                onClick={() => handleDeleteItem(listName, index)}
                            >
                                Delete
                            </Button>,
                        ]}
                    >
                        {item}
                    </List.Item>
                )}
                pagination={{ pageSize: 10 }}
            />
        </Card>
    );

    const renderChinaCityList = () => {
        if (!tiaData) return null;
        
        return (
            <Card
                title="chinaCityList"
                extra={
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => handleAddItem('chinaCityList')}>
                        Add Province
                    </Button>
                }
            >
                <List
                    dataSource={tiaData.chinaCityList}
                    renderItem={(item, index) => (
                        <List.Item
                            actions={[
                                <Button
                                    type="link"
                                    icon={<EditOutlined />}
                                    onClick={() => handleEditItem('chinaCityList', item, index)}
                                >
                                    Edit
                                </Button>,
                                <Button
                                    type="link"
                                    danger
                                    icon={<DeleteOutlined />}
                                    onClick={() => handleDeleteItem('chinaCityList', index)}
                                >
                                    Delete
                                </Button>,
                            ]}
                        >
                            <div style={{ width: '100%' }}>
                                <strong>{item.province}</strong>
                                <div style={{ marginTop: 8, color: '#666' }}>
                                    Cities: {item.cities.join(', ')}
                                </div>
                            </div>
                        </List.Item>
                    )}
                    pagination={{ pageSize: 10 }}
                />
            </Card>
        );
    };

    const renderFacilityList = () => {
        if (!tiaData) return null;
        
        return (
            <Card
                title="facilityList"
                extra={
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => handleAddItem('facilityList')}>
                        Add Facility Type
                    </Button>
                }
            >
                <List
                    dataSource={tiaData.facilityList}
                    renderItem={(item, index) => (
                        <List.Item
                            actions={[
                                <Button
                                    type="link"
                                    icon={<EditOutlined />}
                                    onClick={() => handleEditItem('facilityList', item, index)}
                                >
                                    Edit
                                </Button>,
                                <Button
                                    type="link"
                                    danger
                                    icon={<DeleteOutlined />}
                                    onClick={() => handleDeleteItem('facilityList', index)}
                                >
                                    Delete
                                </Button>,
                            ]}
                        >
                            <div style={{ width: '100%' }}>
                                <strong>{item.facilityType}</strong>
                                <div style={{ marginTop: 8, color: '#666' }}>
                                    Facilities: {item.facility.length} items
                                </div>
                            </div>
                        </List.Item>
                    )}
                    pagination={{ pageSize: 10 }}
                />
            </Card>
        );
    };

    const renderEditModal = () => {
        if (!editingList) return null;

        if (editingList === 'chinaCityList') {
            return (
                <Modal
                    title={editingIndex >= 0 ? 'Edit Province' : 'Add Province'}
                    visible={editModalVisible}
                    onOk={handleModalOk}
                    onCancel={() => {
                        setEditModalVisible(false);
                        form.resetFields();
                    }}
                    width={600}
                >
                    <Form form={form} layout="vertical">
                        <Form.Item
                            name="province"
                            label="Province"
                            rules={[{ required: true, message: 'Please enter province name' }]}
                        >
                            <Input placeholder="Enter province name" />
                        </Form.Item>
                        <Form.Item
                            name="cities"
                            label="Cities (one per line)"
                            rules={[{ required: true, message: 'Please enter cities' }]}
                            getValueFromEvent={(e) => e.target.value}
                            getValueProps={(value) => ({ value: Array.isArray(value) ? value.join('\n') : value })}
                        >
                            <TextArea rows={10} placeholder="Enter cities, one per line" />
                        </Form.Item>
                    </Form>
                </Modal>
            );
        } else if (editingList === 'facilityList') {
            return (
                <Modal
                    title={editingIndex >= 0 ? 'Edit Facility Type' : 'Add Facility Type'}
                    visible={editModalVisible}
                    onOk={handleModalOk}
                    onCancel={() => {
                        setEditModalVisible(false);
                        form.resetFields();
                    }}
                    width={600}
                >
                    <Form form={form} layout="vertical">
                        <Form.Item
                            name="facilityType"
                            label="Facility Type"
                            rules={[{ required: true, message: 'Please enter facility type' }]}
                        >
                            <Input placeholder="Enter facility type" />
                        </Form.Item>
                        <Form.Item
                            name="facility"
                            label="Facilities (one per line)"
                            rules={[{ required: true, message: 'Please enter facilities' }]}
                            getValueFromEvent={(e) => e.target.value}
                            getValueProps={(value) => ({ value: Array.isArray(value) ? value.join('\n') : value })}
                        >
                            <TextArea rows={10} placeholder="Enter facilities, one per line" />
                        </Form.Item>
                    </Form>
                </Modal>
            );
        } else {
            return (
                <Modal
                    title={editingIndex >= 0 ? 'Edit Item' : 'Add Item'}
                    visible={editModalVisible}
                    onOk={handleModalOk}
                    onCancel={() => {
                        setEditModalVisible(false);
                        form.resetFields();
                    }}
                >
                    <Form form={form} layout="vertical">
                        <Form.Item
                            name="value"
                            label="Value"
                            rules={[{ required: true, message: 'Please enter a value' }]}
                        >
                            <Input placeholder="Enter value" />
                        </Form.Item>
                    </Form>
                </Modal>
            );
        }
    };

    if (!tiaData) {
        return <div style={{ padding: 24 }}>Loading...</div>;
    }

    return (
        <div style={{ padding: 24 }}>
            <h1>TIA Data Management</h1>
            <Tabs defaultActiveKey="1">
                <TabPane tab="Type of Travel Agent" key="1">
                    {renderSimpleList('typeOfTravelAgentList', tiaData.typeOfTravelAgentList)}
                </TabPane>
                <TabPane tab="Origin City" key="2">
                    {renderSimpleList('originCityList', tiaData.originCityList)}
                </TabPane>
                <TabPane tab="China City" key="3">
                    {renderChinaCityList()}
                </TabPane>
                <TabPane tab="Currency" key="4">
                    {renderSimpleList('currencyList', tiaData.currencyList)}
                </TabPane>
                <TabPane tab="Control Point" key="5">
                    {renderSimpleList('controlPointList', tiaData.controlPointList)}
                </TabPane>
                <TabPane tab="Type of Facility" key="6">
                    {renderSimpleList('typeOfFacilityList', tiaData.typeOfFacilityList)}
                </TabPane>
                <TabPane tab="Facility" key="7">
                    {renderFacilityList()}
                </TabPane>
                <TabPane tab="Type of Accommodation" key="8">
                    {renderSimpleList('typeOfAccommdation', tiaData.typeOfAccommdation)}
                </TabPane>
            </Tabs>
            {renderEditModal()}
        </div>
    );
}

export default TiaDataManagement;
